import React, { useEffect, useState } from 'react';
import type { StudentType, YourStudentsStateType } from '../../types';
import type { StudentButtonTextType } from '../../../global/buttons/StudentButton';
import type { StudentsTableAccessors } from '../../../global/tables/StudentsTable';
import type { ElementColorType } from '../../../util/types';
import { useI18n } from '@whizz/react-i18n';
import { your_students_state } from '../../types';
import { CollapseSection, ListItem } from '../modules';
import { Loader } from '../../../components';
import { StudentsTable } from '../../../global';
import { mapGroupReportForYourStudents } from '../../../mapper/groupMapper';
import LessonHistory from '../../Reports/LessonHistory';
import type { SectionProps } from '../Home';

interface Props extends SectionProps {
  groupId: number;
}

type HelpState = {
  state: YourStudentsStateType;
  students: StudentType[];
};

const YourStudents = ({ isLoading, groupReport, groupId, open, setOpen }: Props): JSX.Element => {
  const { translate } = useI18n();

  const elements: Record<YourStudentsStateType, {
    color: ElementColorType,
    button: StudentButtonTextType
    columns: StudentsTableAccessors[] | StudentsTableAccessors | null
  }> = {
    slow_progress: {
      color: 'negative',
      button: 'help',
      columns: null,
    },
    failed_2: {
      color: 'negative',
      button: 'help',
      columns: 'lesson_details',
    },
    struggling: {
      color: 'negative',
      button: 'help',
      columns: null,
    },
    good_usage_low_progressions: {
      color: 'negative',
      button: 'help',
      columns: [ 'minutes', 'progressions' ],
    },
    reassessed: {
      color: 'negative',
      button: 'help',
      columns: 'progress',
    },
  };

  const [ data, setData ] = useState<StudentType[]>([]);
  const [ lessonHistoryId, setLessonHistoryId ] = useState<number | null>(null);

  useEffect(() => {
    if (null == groupReport) {
      return;
    }

    setData(mapGroupReportForYourStudents(groupReport));
  }, [ groupReport ]);

  const getFilteredByState = (data: StudentType[], state: YourStudentsStateType) => {
    return data.filter((student) => student.yourStudentsStates && student.yourStudentsStates.includes(state));
  };

  const helpStates: HelpState[] = your_students_state.map((state: YourStudentsStateType) => {
    const studentsFilteredByState = getFilteredByState(data, state);

    if (0 === studentsFilteredByState.length) {
      return null;
    }
    return {
      state: state,
      students: studentsFilteredByState,
    };
  }).filter((value): value is HelpState => !!value);

  if (0 === helpStates.length) {
    return <></>;
  }

  const lessonHistoryStudent = lessonHistoryId ? groupReport?.students.find((student) => student.id === lessonHistoryId) : null;

  return (
    <CollapseSection
      headline={isLoading ? '' : translate('pages.HOME.YOUR_STUDENTS.HEADLINE')}
      strapline={isLoading ? '' : translate('pages.HOME.YOUR_STUDENTS.STRAPLINE')}
      dataCy={'home_your_students'}
      open={open}
      setOpen={setOpen}
    >
      {isLoading
        ? (<Loader />)
        : <>
          { lessonHistoryStudent ? (
            <LessonHistory
              student={{ childId: lessonHistoryStudent.id, fullName: lessonHistoryStudent.lastName ? `${lessonHistoryStudent.firstName} ${lessonHistoryStudent.lastName}` : lessonHistoryStudent.firstName }}
              groupId={groupId}
              dateRange={{
                startDate: 'first-assessment',
                endDate: null,
              }}
              onClose={() => setLessonHistoryId(null)}
            />)
            : null }
          <h2 className='font-bold mb-lg'>{translate('pages.HOME.YOUR_STUDENTS.SUB_HEADLINE')}</h2>
          <div className={'space-y-12 mb-md'}>
            {(helpStates.map((value) => {
              const { state, students } = value;
              return (
                <ListItem
                  key={`your_students_state_${state}`}
                  students={students}
                  description={translate(`pages.HOME.YOUR_STUDENTS.${state.toUpperCase()}.DESCRIPTION`, { count: students.length })}
                  color={elements[state].color}
                  moreInfo={translate(`pages.HOME.YOUR_STUDENTS.${state.toUpperCase()}.INFO`)}
                  data-cy={`yourStudents-display-${state}`}
                >
                  <StudentsTable
                    id={`your_students-list_${state}`}
                    data={students}
                    column={elements[state].columns}
                    color={elements[state].color}
                    headerMessage={translate(`pages.HOME.YOUR_STUDENTS.${state.toUpperCase()}.HELP`)}
                    onStudentClick={state !== 'reassessed' ? (id) => setLessonHistoryId(id) : undefined}
                    state={state}
                    data-cy={`yourStudents-table-${state}`}
                  />
                </ListItem>
              );
            }))}
          </div>
        </>
      }
    </CollapseSection>
  );
};

YourStudents.displayName = 'YourStudents';

export default YourStudents;
